import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Brain, Gamepad2, Heart, Sparkles, Target, Users } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useUIStore } from '../store/uiStore';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { useDashboardData } from '../hooks/useDashboardData';
import { ACENTOS, type SlotAcento } from '../types/dashboard';
import {
  BannerSinRed,
  DashboardError,
  DashboardSkeleton,
  NivelCard,
} from '../components/dashboard/Estados';
import { PrimerUso } from '../components/dashboard/PrimerUso';
import { HistorialActividad } from '../components/dashboard/HistorialActividad';
import type { LucideIcon } from 'lucide-react';

const cardBase = 'rounded-card border border-v-outline bg-v-surface p-4 shadow-[0_1px_3px_rgb(0_0_0/0.05)]';

interface Accion {
  titulo: string;
  descripcion: string;
  icono: LucideIcon;
  slot: SlotAcento;
  href: string;
  flag?: string;
}

/**
 * Accesos del dia. Se han quedado los cinco que NO estan en la barra inferior
 * fija... salvo "Respirar y meditar", que si esta y se mantiene por ser el
 * modulo de marca.
 */
const ACCIONES: Accion[] = [
  { titulo: 'Respirar y meditar', descripcion: 'Ejercicios de calma', icono: Heart, slot: 'rosa', href: '/ejercicios', flag: 'ejercicios_juegos' },
  { titulo: 'Evaluación', descripcion: 'Cuestionario de bienestar', icono: Brain, slot: 'violeta', href: '/evaluacion', flag: 'evaluaciones' },
  { titulo: 'Juegos de calma', descripcion: 'Aprende jugando', icono: Gamepad2, slot: 'lima', href: '/juegos', flag: 'ejercicios_juegos' },
  { titulo: 'Comunidad', descripcion: 'Acompañamiento y foros', icono: Users, slot: 'verde', href: '/comunidad', flag: 'comunidad' },
  { titulo: 'Mis metas', descripcion: 'Avance de tus metas', icono: Target, slot: 'ambar', href: '/metas' },
];

/**
 * Cifras de uso. Se ven DENTRO del panel plegable de "Lo que has hecho", no
 * sueltas en la pantalla de entrada.
 */
const STATS: { key: StatKey; label: string; slot: SlotAcento }[] = [
  { key: 'ejerciciosCompletados', label: 'Ejercicios', slot: 'rosa' },
  { key: 'evaluacionesCompletadas', label: 'Evaluaciones', slot: 'violeta' },
  { key: 'postsComunidad', label: 'Posts', slot: 'verde' },
];

/** Solo las claves numericas de las estadísticas. */
type StatKey = 'ejerciciosCompletados' | 'evaluacionesCompletadas' | 'postsComunidad';

const ICONOS_STATS: Record<StatKey, LucideIcon> = {
  ejerciciosCompletados: Heart,
  evaluacionesCompletadas: Brain,
  postsComunidad: Users,
};

/**
 * Tile de accion. Un solo componente con dos orientaciones:
 *  - movil: horizontal (icono a la izquierda), porque en una columna estrecha
 *    el texto a la derecha cabe mejor;
 *  - escritorio: vertical (icono arriba, titulo y descripcion debajo), que es
 *    lo que aprovecha una rejilla de 3 columnas.
 * El breakpoint decide con clases, no con estado: evita re-renderizar y duplicar
 * el markup.
 */
function AccionCard({ accion }: { accion: Accion }) {
  const navigate = useNavigate();
  const { bg, container, ink } = ACENTOS[accion.slot];

  return (
    <button
      type="button"
      onClick={() => navigate(accion.href)}
      className={`${cardBase} group flex min-h-14 w-full items-center gap-3 text-left
                  transition-transform active:scale-[0.98]
                  md:min-h-[8.5rem] md:flex-col md:items-start md:gap-0 md:p-5`}
    >
      {/* El icono va en el container con tinta 700, nunca blanco sobre el
          fill 500: blanco sobre rosa es 3.5:1 y sobre verde 2.3:1. */}
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl transition-transform
                   group-active:scale-95 md:h-12 md:w-12"
        style={{ backgroundColor: container }}
      >
        <accion.icono className="h-5 w-5 md:h-6 md:w-6" style={{ color: ink }} aria-hidden />
      </span>

      <span className="min-w-0 flex-1 md:mt-3">
        <span className="block text-sm font-bold text-v-on-surface md:text-base">
          {accion.titulo}
        </span>
        <span className="mt-0.5 block text-xs text-v-on-surface-variant">{accion.descripcion}</span>
      </span>

      {/* Barra de acento: el unico uso del 500 en la tarjeta. En movil va a la
          derecha (el hueco que deja el flex-1); en escritorio, abajo del todo. */}
      <span
        aria-hidden
        className="h-1 w-8 shrink-0 rounded-full md:mt-4 md:w-full"
        style={{ backgroundColor: bg }}
      />
    </button>
  );
}

/**
 * Cada tarjeta es su propio `<dl>`: la cifra y su etiqueta quedan asociadas
 * semanticamente sin recurrir a `display: contents` sobre un wrapper (que
 * SACABA elementos del arbol de accesibilidad en varios navegadores).
 *
 * Sin `cardBase`: estas cifras viven DENTRO de la tarjeta del panel, y anidar
 * tarjeta con borde y sombra dentro de otra tarjeta con borde y sombra se lee
 * como un error de maquetacion. Aqui el fondo de acento es toda la superficie.
 */
function StatCard({
  icono: Icono,
  label,
  valor,
  slot,
}: {
  icono: LucideIcon;
  label: string;
  valor: number;
  slot: SlotAcento;
}) {
  const { bg, container, ink } = ACENTOS[slot];

  return (
    <dl
      className="flex flex-col justify-between rounded-xl p-3"
      style={{ backgroundColor: container }}
    >
      <div className="flex items-start justify-between">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-v-surface-70">
          <Icono className="h-5 w-5" style={{ color: ink }} aria-hidden />
        </span>
        <span aria-hidden className="h-1 w-8 rounded-full" style={{ backgroundColor: bg }} />
      </div>
      <div className="mt-3">
        <dd className="text-2xl font-extrabold tabular-nums" style={{ color: ink }}>
          {valor}
        </dd>
        <dt className="text-xs font-medium text-v-on-surface-variant">{label}</dt>
      </div>
    </dl>
  );
}

/**
 * Hero: la primera pantalla del inicio, y lo primero que ve el estudiante.
 *
 * EL DEGRADADO ES PROPIO, Y NO `--v-grad-*`
 * -----------------------------------------
 * Antes era `#00574C -> #134E7B` con texto blanco: 8.51:1, medido y correcto.
 * Pero es un verde oil y un navy: serio, y el color no jugaba a favor de nada.
 *
 * Los `--v-grad-*` no sirven aqui porque llevan texto blanco SIEMPRE y no
 * cambian con el tema. Con esos dos stops, el blanco cae a **1.61:1**: no hay
 * forma de aclararlos sin inventar una tinta nueva.
 *
 * La solucion es cambiar la tinta en vez de bajar el fondo: `--v-hero-a/b` y
 * `--v-hero-ink` son los tres tokens del hero, y los tres cambian en `.dark`
 * juntos. Con tinta oscura sobre aqua y cielo:
 *
 *   claro  8.62:1 con la tinta, 4.74:1 al 75% (el `<p>` de 14px)
 *   oscuro 8.15:1 con la tinta, 5.36:1 al 75%
 *
 * El par esta en `check:contrast`. El 75% no se puede medir ahi (no hay alfa
 * que componer en un token) y lo mide `contraste-marca.spec.ts` sobre el color
 * ya resuelto en el navegador.
 *
 * LA VIDA
 * -------
 * Tres manchas difusas con una deriva muy lenta. Se quedan a la derecha y abajo:
 * el texto vive a la izquierda, y una mancha debajo de un saludo no es vida, es
 * ruido.
 *
 * `motion-reduce:animate-none` no es un adorno: sin esto la deriva sigue
 * corriendo para quien pidio no tener movimiento en la app.
 */
function Hero({ primera }: { primera: string }) {
  return (
    <section
      className="relative overflow-hidden rounded-b-[1.75rem] px-5 pb-4 pt-4
                 md:rounded-2xl md:px-8 md:pb-6 md:pt-6"
      style={{
        backgroundImage: 'linear-gradient(135deg, var(--v-hero-a) 0%, var(--v-hero-b) 100%)',
      }}
    >
      {/* Los tonos NO son del eje vital: mint, ice y lima sobre un fondo aqua
          son los mismos tonos que el fondo, y una mancha del color del suelo no
          es una mancha. Aqui van violeta y lima, que es lo unico de la paleta
          que se separa de un aqua-celeste, mas una blanca de luz.

          LAS TRES EN SU PROPIA COLUMNA, y no por estetica. El envoltorio
          empieza en el 55% del ancho y el saludo se limita al 50%: hay un 5% de
          margen entre los dos, asi que no pueden solaparse. Moviendo las manchas
          con `right-*` habia que perseguirlas motor por motor: en Firefox el
          texto se dibuja un poco mas ancho y una mancha que en Chromium no
          tocaba nada caia encima del "Hola". Con la division del ancho, el
          solape es IMPOSIBLE por construccion y no depende de la metrica de la
          fuente. Lo verifica `dashboard.spec.ts` con las cajas reales de los
          glifos.

          Una sola animacion authored, la misma deriva con tres duraciones: se
          nota como que el color respira, no como que tres cosas se mueven. El
          keyframe esta en `index.css` porque Tailwind no genera `@keyframes`
          para una clase arbitraria. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-[55%] overflow-hidden"
      >
        <div className="animate-[deriva_18s_ease-in-out_infinite_alternate] motion-reduce:animate-none
                    absolute -right-10 -top-12 h-52 w-52 rounded-full bg-v-violeta-500 opacity-35 blur-3xl
                    md:h-80 md:w-80" />
        <div className="animate-[deriva_24s_ease-in-out_infinite_alternate] motion-reduce:animate-none
                    absolute -bottom-8 right-4 h-40 w-40 rounded-full bg-v-lima-500 opacity-40 blur-3xl
                    md:right-16 md:h-64 md:w-64" />
        <div className="animate-[deriva_21s_ease-in-out_infinite_alternate] motion-reduce:animate-none
                    absolute -bottom-4 right-28 h-28 w-28 rounded-full bg-white opacity-30 blur-3xl
                    md:right-52 md:h-40 md:w-40" />
      </div>

      {/* Alto compacto: con `pt-5 pb-6` el bloque ocupaba unos 108px en movil
          para dos lineas de texto y el tercio de abajo se leia como un error de
          maquetacion. `leading-tight` recorta otros 6px sin tocar el tamano:
          la jerarquia la da el peso, no el interlineado.

          El logo va sobre una PLACA blanca, y no suelto, por una razon medida:
          el navy del logo es casi el mismo color que el extremo claro del hero en
          oscuro (`#1b3a63`), asi que suelto daba ~1.2:1 y desaparecia. Un
          filtro de brillo lo arreglaria en un tema y lo romperia en el otro. La
          placa es la solucion que funciona en los dos, y de paso es como se ve
          un logotipo sobre fondo de color.

          Es el mismo `public/logo_vita360.png` que ya usan Login, Registro y
          Welcome: no se anade ningun archivo. */}
      <div className="relative flex items-center justify-between gap-3">
        <div className="min-w-0 max-w-[52%]">
          <p className="text-sm font-medium leading-snug text-[color:var(--v-hero-ink)] opacity-75">
            Tu espacio de bienestar
          </p>
          <h1 className="mt-0.5 text-2xl font-extrabold leading-tight tracking-tight text-[color:var(--v-hero-ink)] md:text-3xl">
            Hola, {primera}
          </h1>
        </div>

        <img
          src="/logo_vita360.png"
          alt="Vita360"
          width={1236}
          height={428}
          className="h-8 w-auto shrink-0 rounded-lg bg-white p-1.5 shadow-[0_2px_10px_-2px_rgb(0_0_0/0.25)] md:h-11"
        />
      </div>
    </section>
  );
}

function TipDelDia() {
  return (
    <section className={`${cardBase} flex items-start gap-3`} aria-labelledby="titulo-tip">
      <span
        className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
        style={{ backgroundColor: '#B8F5EC' }}
      >
        <Sparkles className="h-5 w-5" style={{ color: '#00574C' }} aria-hidden />
      </span>
      <div className="min-w-0">
        <span
          className="rounded-full px-2.5 py-0.5 text-[11px] font-bold"
          style={{ backgroundColor: '#B8F5EC', color: '#00574C' }}
        >
          Tip del día
        </span>
        <p id="titulo-tip" className="mt-1.5 text-sm leading-relaxed text-v-on-surface">
          Dedica 5 minutos cada mañana a respirar profundo. Puede reducir el estrés y mejorar tu
          concentración durante todo el día.
        </p>
      </div>
    </section>
  );
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const gamificacionVisible = useUIStore((s) => s.gamificacionVisible);
  const { isEnabled } = useFeatureFlags();

  // Redirect en el RENDER, no en un useEffect: antes el `return` temprano
  // dejaba `isLoading === true` y el psicologo veia un spinner durante la
  // redireccion. `activo=false` evita ademas pedir datos que no se van a usar.
  const redirigiendo = user?.rol === 'PSICOLOGO';
  const { estado, stats, historial, reintentar } = useDashboardData(!redirigiendo);

  if (redirigiendo) return <Navigate to="/dashboard-psicologo" replace />;

  if (estado === 'cargando') return <DashboardSkeleton />;

  // `sin-red` sin datos previos cae tambien en la tarjeta de error: sin cache
  // no hay nada que mostrar, y quedarse en el skeleton seria un bucle
  // infinito. El banner se antepone para explicar el por que.
  const fallo = estado === 'error' || estado === 'sin-red';
  if (fallo) {
    return (
      <div className="mx-auto w-full max-w-md space-y-3 md:max-w-3xl">
        {estado === 'sin-red' && <BannerSinRed />}
        <DashboardError onRetry={reintentar} />
      </div>
    );
  }

  if (estado === 'primer-uso') return <PrimerUso />;
  if (!stats) return <DashboardSkeleton />;

  const primera = user?.nombre?.trim().split(/\s+/)[0] || 'Estudiante';
  const visibles = ACCIONES.filter((a) => (a.flag ? isEnabled(a.flag) : true));

  return (
    <div className="mx-auto w-full max-w-md space-y-4 md:max-w-3xl">
      <Hero primera={primera} />
      {gamificacionVisible && (
        <>
          <NivelCard stats={stats} mostrarBarra={false} />

          {/* P7: historial + cifras. Va dentro del opt-in por la misma razon que
              el nivel: si el estudiante apago la gamificacion, no quiere ver
              marcadores de progreso, y las cifras lo son. */}
          <HistorialActividad
            actividades={historial}
            resumen={
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {STATS.map(({ key, label, slot }) => (
                  <StatCard
                    key={key}
                    icono={ICONOS_STATS[key] ?? Target}
                    label={label}
                    valor={Number(stats[key] ?? 0)}
                    slot={slot}
                  />
                ))}
              </div>
            }
          />
        </>
      )}

      <section aria-labelledby="titulo-acciones" className="space-y-3">
        <h2 id="titulo-acciones" className="text-base font-bold text-v-on-surface">
          ¿Qué hacemos hoy?
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          {visibles.map((accion) => (
            <AccionCard key={accion.href} accion={accion} />
          ))}
        </div>
      </section>

      <TipDelDia />
    </div>
  );
}
